/**
 * TrustScreen — Verifies PWA conditions before scheduling a retreat.
 * Answers: "Will the bell ring?"
 * UI-INV-005: Leaf component, props only (trust checks called on mount via useEffect).
 * Task 10 (Session E).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import styles from "./TrustScreen.module.css";
import { runTrustChecks, type TrustCheck, type TrustResult } from "../services/trustVerifier";
import { STORAGE_KEYS, TIMING } from "../constants";
import { TEST_IDS } from "../testing/testIds";

const TTL_MS = TIMING.DAY_MS;

function readCachedVerifiedAt(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.TRUST_VERIFIED_AT);
    if (!raw) return null;
    const n = Number(raw);
    return isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function saveVerifiedAt(ts: number): void {
  try {
    localStorage.setItem(STORAGE_KEYS.TRUST_VERIFIED_AT, String(ts));
  } catch { /* quota */ }
}

function formatVerifiedTime(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

type Props = {
  onContinue: () => void;
  onBack: () => void;
};

export function TrustScreen({ onContinue, onBack }: Props) {
  const [result, setResult] = useState<TrustResult | null>(null);
  const [cachedVerifiedAt, setCachedVerifiedAt] = useState<number | null>(null);
  const [showExplanations, setShowExplanations] = useState(false);
  const explanationsRef = useRef<HTMLDivElement>(null);

  const runChecks = useCallback(async () => {
    const r = await runTrustChecks();
    setResult(r);
    saveVerifiedAt(r.verifiedAt);
    setCachedVerifiedAt(r.verifiedAt);
  }, []);

  useEffect(() => {
    const cached = readCachedVerifiedAt();
    const isWithinTTL = cached !== null && Date.now() - cached < TTL_MS;
    if (isWithinTTL) {
      setCachedVerifiedAt(cached);
    }
    // Always run checks: show cached time but get fresh result
    void runChecks();
  }, [runChecks]);

  const goodCount = result?.checks.filter((c) => c.status === "good").length ?? 0;
  const verifiedAtLabel =
    cachedVerifiedAt !== null
      ? `verified ${formatVerifiedTime(cachedVerifiedAt)} today · ${goodCount} check${goodCount !== 1 ? "s" : ""} passed`
      : "Checking…";

  return (
    <div className={styles.screen} data-testid="trust-screen">
      <div className={styles.meta}>
        <button className={styles.backBtn} onClick={onBack} type="button" aria-label="Back">
          ← Back
        </button>
        <span className={styles.metaRight}>Bell check · 1 of 1</span>
      </div>

      <div className={styles.content}>
        <p className={styles.eyebrow}>Before we begin</p>
        <h1 className={styles.heading}>Will the bell ring?</h1>
        <p className={styles.whisper}>
          A retreat that fails halfway is worse than one not begun. We test what we can. Then you decide.
        </p>

        {/* Check list */}
        <div className={styles.checkList} data-testid={TEST_IDS.TRUST_CHECK_LIST}>
          {(result?.checks ?? []).map((check) => (
            <CheckRow key={check.id} check={check} />
          ))}
          {!result && (
            <p className={styles.loading}>Checking…</p>
          )}
        </div>

        {/* Last verified strip */}
        {cachedVerifiedAt !== null && (
          <div className={styles.verifiedStrip}>
            <span className={styles.verifiedDot} />
            <span className={styles.verifiedText}>
              Verified · {verifiedAtLabel} · we re-test at the start of every retreat.
            </span>
          </div>
        )}

        {/* Reassurance card */}
        <div className={styles.reassuranceCard}>
          <p className={styles.reassuranceText}>
            If the wake bell fails — the schedule continues in the background. You can open the app
            at any time and return to wherever the retreat is.
          </p>
        </div>

        <button
          className={styles.continueCta}
          onClick={onContinue}
          type="button"
          data-testid={TEST_IDS.TRUST_CONTINUE_BTN}
        >
          Continue scheduling
        </button>

        <div className={styles.secondaryLinks}>
          <button className={styles.secondaryLink} onClick={runChecks} type="button">
            Re-run the check
          </button>
          <span className={styles.dot}>·</span>
          <button
            className={styles.secondaryLink}
            type="button"
            data-testid={TEST_IDS.TRUST_EXPLAIN_TOGGLE}
            onClick={() => {
              setShowExplanations((v) => !v);
              requestAnimationFrame(() => explanationsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
            }}
          >
            {showExplanations ? "Hide explanations" : "What does each check do?"}
          </button>
        </div>

        {showExplanations && result && (
          <div ref={explanationsRef} className={styles.explanations} data-testid={TEST_IDS.TRUST_EXPLANATIONS}>
            {result.checks.map((check) => (
              <div key={check.id} className={styles.explanationRow}>
                <p className={styles.explanationLabel}>{check.label}</p>
                <p className={styles.explanationDetail}>{check.detail}</p>
                {check.advice && (
                  <p className={styles.explanationAdvice}>{check.advice}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CheckRow({ check }: { check: TrustCheck }) {
  return (
    <div className={`${styles.checkRow} ${check.status === "caution" ? styles.checkRowCaution : ""}`}>
      <span
        className={`${styles.statusCircle} ${check.status === "good" ? styles.statusGood : styles.statusCaution}`}
        aria-label={check.status === "good" ? "Good" : "Caution"}
      >
        {check.status === "good" ? "✓" : "!"}
      </span>
      <div className={styles.checkContent}>
        <p className={styles.checkLabel}>{check.label}</p>
        <p className={styles.checkDetail}>{check.detail}</p>
        {check.advice && <p className={styles.checkAdvice}>{check.advice}</p>}
      </div>
    </div>
  );
}
